<script setup lang="ts">
  import { IApenABCOverviewItem, INewsOverviewItem } from "@/repositories/overview.repository"
  import { OverviewService } from "@/services/overview.service"
  import { IOverviewCmsData, OverviewType } from "@/view-models/overview.model"
  import Chip from "./components/button/ChipButton.vue"
  import LoadMoreButton from "@/components/LoadMoreButton.vue"
  import Loader from "@/components/LoaderComponent.vue"
  import ApenABCOverviewDesktopImageCard from "@/components/cards/ApenABCOverviewDesktopImageCard.vue"
  import ApenABCOverviewMobileImageCard from "@/components/cards/ApenABCOverviewMobileImageCard.vue"
  import GeneralOverviewDesktopImageCard from "@/components/cards/GeneralOverviewDesktopImageCard.vue"
  import GeneralOverviewDesktopNoImageCard from "@/components/cards/GeneralOverviewDesktopNoImageCard.vue"
  import GeneralOverviewMobileImageCard from "@/components/cards/GeneralOverviewMobileImageCard.vue"
  import GeneralOverviewMobileNoImageCard from "@/components/cards/GeneralOverviewMobileNoImageCard.vue"
  import NewsOverviewDesktopImageCard from "@/components/cards/NewsOverviewDesktopImageCard.vue"
  import NewsOverviewMobileImageCard from "@/components/cards/NewsOverviewMobileImageCard.vue"
  import { SortField } from "@/composables/useFetchWithPagination"

  const cmsData = CMSDataProvider.get<IOverviewCmsData>(CMS_DATA_ID.OVERVIEW)

  const { isLoading, orderByDescending, results, total, loadMore } = useFetchWithPagination(
    OverviewService.Get,
    {
      culture: cmsData.culture,
      pageId: cmsData.context,
      perPage: 12,
      sortBy: cmsData.overviewType === OverviewType.ApenABC ? SortField.Name : SortField.Date,
      orderByDescending: cmsData.overviewType === OverviewType.ApenABC ? false : true
    }
  )

  const { filteredResults, availableTags, isTagActive, toggleActiveTag } = useResultsFilteringByTag(results);

  const resultsText = computed(() => {
    let cmsString ="";
    if(cmsData.resultTextMultiple && cmsData.resultTextSingle)
    {
       cmsString =  (total.value === 1 ? cmsData.resultTextSingle : cmsData.resultTextMultiple);
       return cmsString.replaceAll("{aantal}", results.value.length.toString()).replaceAll("{totaal}", total.value.toString());
    }
    return "";
  })

  const remaining = computed(() => {
    return (total.value - results.value.length)
  })
</script>

<template>
  <div
    class="overview-scoped container flex flex-col gap-4 max-md:px-5 md:gap-3"
  >
    <div class="mt-10 flex flex-col md:flex-row justify-center items-center gap-2">
      <Chip
        v-for="tag in availableTags"
        :key="tag"
        :text="tag"
        :is-active="isTagActive(tag)"
        @click="toggleActiveTag(tag)"
      />
    </div>
    <div class="flex flex-col md:flex-row md:justify-between md:items-center general-label-text md:mb-6 ">
      <p
        v-if="cmsData.overviewType !== OverviewType.ApenABC"
        class="text-center max-md:mb-4"
        v-text="resultsText"
      />
      <div class="flex flex-wrap justify-center md:justify-between gap-4 items-center">
        <p class="hidden justify-center md:flex"> {{ cmsData.sortByLabel }}</p>
        <select
          v-if="filteredResults.length > 0 && cmsData.overviewType === OverviewType.ApenABC"
          v-model="orderByDescending"
          class="sort-select-text form-select h-[40px] w-[240px] border-0 rounded-[8px] mb-4 md:mb-0 transition ease-in-out duration-100 bg-sg-600 hover:cursor-pointer hover:border-primary"
        >
          <option :value="false">
            {{ cmsData.nameAscendingLabel }}
          </option>
          <option :value="true">
            {{ cmsData.nameDescendingLabel }}
          </option>
        </select>
        <select
          v-else
          v-model="orderByDescending"
          class="sort-select-text form-select h-[40px] w-[240px] border-0 rounded-[8px] mb-4 md:mb-0 transition ease-in-out duration-100 bg-sg-600 hover:cursor-pointer hover:border-primary"
        >
          <option :value="true">
            {{ cmsData.dateDescendingLabel }}
          </option>
          <option :value="false">
            {{ cmsData.dateAscendingLabel }}
          </option>
        </select>
      </div>
    </div>
    <div class="flex flex-col gap-12">
      <div
        :class="{ 'mb-40': remaining == 0 }"
        class="grid md:auto-rows-overview-general lg:grid-cols-overview-general md:justify-center items-center gap-3 md:gap-6"
      >
          <template
            v-for="item in filteredResults"
            :key="item.id"
          >
            <template v-if="item.cardType === CardType.ApenABC">
              <template v-if="item.image">
                <ApenABCOverviewDesktopImageCard 
                  :item="item as IApenABCOverviewItem" 
                  :apen-abc-label="cmsData.apenAbcLabel" 
                  />
                <ApenABCOverviewMobileImageCard 
                  :item="item as IApenABCOverviewItem"
                  :apen-abc-label="cmsData.apenAbcLabel"  
                />
              </template>
              <template v-else>
                <GeneralOverviewDesktopNoImageCard :item />
                <GeneralOverviewMobileNoImageCard :item />
              </template>
            </template>
            <template v-else-if="item.cardType === CardType.NewsArticle">
              <template v-if="item.image">
                <NewsOverviewDesktopImageCard :item="item as INewsOverviewItem" />
                <NewsOverviewMobileImageCard :item="item as INewsOverviewItem" />
              </template>
              <template v-else>
                <GeneralOverviewDesktopNoImageCard :item />
                <GeneralOverviewMobileNoImageCard :item />
              </template>
            </template>
            <template v-else>
              <template v-if="item.image">
                <GeneralOverviewDesktopImageCard :item />
                <GeneralOverviewMobileImageCard :item />
              </template>
              <template v-else>
                <GeneralOverviewDesktopNoImageCard :item />
                <GeneralOverviewMobileNoImageCard :item />
              </template>
            </template>
          </template>
      </div>
      <Loader
          v-if="isLoading"
          centered
        />
      <LoadMoreButton
        v-if="remaining > 0"
        class="mb-40 general-label-text"
        :number-of-items="remaining.toString()"
        @load-more="loadMore"
      />
    </div>
  </div>
</template>

